/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import React, { Fragment, lazy, useState, useEffect } from 'react';
import {
	type FieldProps,
	type FieldChildrenProps,
	type NativeDatePickerProps,
	type RenderFn,
} from '@atlassian/forge-ui-types';
import { FormStateChangeNotifier } from '../form';
import { useIsInForm } from '../utils/useIsInForm';
import { Label } from '../label';
import { FieldContainer } from '../utils/FieldContainer';

const AKDatePicker = React.lazy(() =>
	import(
		/* webpackChunkName: '@atlaskit-internal_.datetime-picker' */
		'@atlaskit/datetime-picker'
	).then((module) => ({
		default: module.DatePicker,
	})),
);

const AKField = React.lazy(() =>
	import(
		/* webpackChunkName: '@atlaskit-internal_.form' */
		'@atlaskit/form'
	).then((module) => ({ default: module.Field })),
);

const AKHelperMessage = lazy(() =>
	import(
		/* webpackChunkName: '@atlaskit-internal_.form' */
		'@atlaskit/form'
	).then((module) => ({
		default: module.HelperMessage,
	})),
);

const AKErrorMessage = React.lazy(() =>
	import(
		/* webpackChunkName: '@atlaskit-internal_.form' */
		'@atlaskit/form'
	).then((module) => ({ default: module.ErrorMessage })),
);

const validate = (value: any) => (!value ? 'EMPTY' : undefined);

const getDateFormat = (locale: string) => {
	const formatObj = new Intl.DateTimeFormat(locale).formatToParts(
		new Date(),
	) as Intl.DateTimeFormatPart[];

	return formatObj
		.map((obj) => {
			switch (obj.type) {
				case 'day':
					return 'DD';
				case 'month':
					return 'MM';
				case 'year':
					return 'YYYY';
				default:
					return obj.value;
			}
		})
		.join('');
};

export const DatePicker = (props: Parameters<RenderFn>[0]) => {
	const [locale, setLocale] = useState('en-US');

	const { name, label, defaultValue, description, placeholder, isRequired, onChange } = props
		.forgeDoc.props as NativeDatePickerProps;

	useEffect(() => {
		if (window.navigator.languages && locale !== window.navigator.languages[0]) {
			setLocale(window.navigator.languages[0]);
		}
	}, [locale]);

	const [value, setValue] = useState(defaultValue || '');
	const isInForm = useIsInForm();

	if (!isInForm) {
		return (
			<FieldContainer>
				<Label name={name} isRequired={isRequired} label={label} />
				<AKDatePicker
					onChange={(date: string) => {
						setValue(date);
						onChange?.(date);
					}}
					value={value}
					placeholder={placeholder}
					dateFormat={getDateFormat(locale)}
				/>
				{description && <AKHelperMessage>{description}</AKHelperMessage>}
			</FieldContainer>
		);
	}

	return (
		<FieldContainer>
			<AKField
				name={name}
				label={label}
				defaultValue={defaultValue}
				isRequired={isRequired}
				validate={isRequired ? validate : undefined}
			>
				{({ fieldProps, error }: FieldChildrenProps & { error?: string }) => {
					const {
						id,
						isDisabled,
						isInvalid,
						onBlur,
						onFocus,
						'aria-invalid': ariaInvalid,
						'aria-labelledby': ariaLabelledBy,
					} = fieldProps as FieldProps;
					return (
						<Fragment>
							<FormStateChangeNotifier name={name} value={fieldProps.value} />
							<AKDatePicker
								id={id}
								isDisabled={isDisabled}
								onBlur={onBlur}
								onFocus={onFocus}
								onChange={(date: string) => {
									fieldProps.onChange(date);
									onChange?.(date);
								}}
								value={fieldProps.value || ''}
								placeholder={placeholder}
								dateFormat={getDateFormat(locale)}
								isInvalid={isInvalid || Boolean(error)}
								aria-invalid={ariaInvalid}
								aria-labelledby={ariaLabelledBy}
							/>
							{error === 'EMPTY' && <AKErrorMessage>This field is required.</AKErrorMessage>}
							{description && <AKHelperMessage>{description}</AKHelperMessage>}
						</Fragment>
					);
				}}
			</AKField>
		</FieldContainer>
	);
};
